import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WhatsappTransport } from "@school-sales-ai/shared";
import {
  listClientsApi,
  getClientApi,
  updateClientTransportApi,
  disconnectClientWhatsappApi,
  listPlansApi,
  updateClientApi,
  setClientActiveApi,
  setClientAccountManagerApi,
  setSiteHostnamesApi,
  type ClientProfileInput,
} from "@/lib/api";

const CLIENTS_KEY = ["super-admin", "clients"] as const;
// The account manager's portfolio (GET /crm/clients/mine). The mutations below are
// reachable from their Clientes screen and from the wizard they now run, so both
// lists have to be refreshed — otherwise a school they just created or renamed keeps
// showing the old value until a reload.
const MY_CLIENTS_KEY = ["my-clients"] as const;

export function useClients() {
  return useQuery({
    queryKey: CLIENTS_KEY,
    queryFn: listClientsApi,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ["super-admin", "plans"],
    queryFn: listPlansApi,
    staleTime: 5 * 60_000,
  });
}

// Creating a client is step 1 of the onboarding wizard, which needs it followed by two
// more writes in a fixed order — see `useCreateClientForOnboarding` in useOnboarding.ts.
// There is no standalone create hook, so there is no second creation path.

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ClientProfileInput & { id: string }) =>
      updateClientApi(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_CLIENTS_KEY });
    },
  });
}

/** Super-admin: the «Sitio web» domains of a client (Decision 8 — connecting a domain is ours). */
export function useSetSiteHostnames() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hostnames }: { id: string; hostnames: string[] }) => setSiteHostnamesApi(id, hostnames),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useSetClientActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setClientActiveApi(id, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_CLIENTS_KEY });
    },
  });
}

export function useSetClientAccountManager() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      accountManagerId,
    }: {
      id: string;
      accountManagerId: string | null;
    }) => setClientAccountManagerApi(id, accountManagerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_CLIENTS_KEY });
    },
  });
}

export function useUpdateClientTransport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transport }: { id: string; transport: WhatsappTransport }) =>
      updateClientTransportApi(id, transport),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["super-admin", "clients"] });
    },
  });
}

// Platform-side WhatsApp disconnect. Refreshes the clients list so the row's connection state —
// and therefore whether the transport switch will be accepted — reflects the new reality.
export function useDisconnectClientWhatsapp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => disconnectClientWhatsappApi(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ["super-admin", "clients", id],
    queryFn: () => getClientApi(id!),
    enabled: !!id,
  });
}
